import { useCallback, useEffect, useMemo, useState } from "react";

import { useAppSelector } from "../../store";

import { PureVoidFunc } from "../../helpers/functions/types";

import { DriverData } from "../types";
import { introGuide, productsFeedGuide, campaignsGuide } from "../guides";

import { awaitMobileMenu, demoLink, mergeDriversDetails } from "../helpers";
import { generateDriver } from "../generateDriver";
import { useRequestHandlers } from "../../app/hooks/useRequestHandlers";
import { useMatch } from "react-router";

export const useGuides = () => {
  const [isDriving, setIsDriving] = useState(false);
  const [tourSkipped, setTourSkipped] = useState(false);

  const isCompanyPage = !!useMatch("/company/:companyId");
  const isOnboarding = useAppSelector((state) => state.user.onboarding);
  const showTour = isCompanyPage && isOnboarding;
  const companyId = useAppSelector((state) => state.company?.company?.id);
  const siteId = useAppSelector((state) => state.site?.id);
  const handlerParams = useRequestHandlers();
  const notify = handlerParams.notify;

  const { status: companyStatus } = useAppSelector((state) => state.company);
  const isCompanyLoaded = companyStatus !== "loading";

  const startDemo = useCallback(
    async (driverData: DriverData, onDestroyed?: PureVoidFunc) => {
      await awaitMobileMenu(driverData.stepsData[0].anchorElement);
      const targetDriver = generateDriver(driverData, notify, onDestroyed);
      targetDriver.drive();
    },
    [notify],
  );

  const feedSkipData = useMemo(() => {
    return companyId && siteId
      ? {
          handlerParams,
          companyId,
          siteId,
          demoLink: demoLink(),
        }
      : null;
  }, [handlerParams, companyId, siteId]);

  const setListableDrivers = useCallback(() => {
    if (!feedSkipData) return [];
    const onboardingGuide = mergeDriversDetails([
      introGuide(),
      productsFeedGuide(),
      campaignsGuide(),
    ]);
    return [onboardingGuide, productsFeedGuide(), campaignsGuide()];
  }, [feedSkipData]);

  const onboardingTour = useMemo(() => {
    if (!feedSkipData) return null;
    return mergeDriversDetails([
      introGuide(), //FIXME - add as variable after demo feed will be avaliable () => setTourSkipped(true), feedSkipData
      productsFeedGuide(),
      campaignsGuide(),
    ]);
  }, [feedSkipData]);

  useEffect(() => {
    if (
      showTour &&
      companyId &&
      siteId &&
      onboardingTour &&
      !isDriving &&
      !tourSkipped &&
      isCompanyLoaded
    ) {
      setIsDriving(true);
      startDemo(onboardingTour.driverData, () => setTourSkipped(true));
    }
  }, [
    onboardingTour,
    isDriving,
    startDemo,
    tourSkipped,
    companyId,
    showTour,
    siteId,
    isCompanyLoaded,
  ]);

  return { startDemo, setListableDrivers };
};
